import api from "src/api/axiosInstance";
import { showToast } from "src/components/ToastProvider";
import ToastTypes from "src/enum/ToastTypes";

/**
 * Fetches supported payment gateways from the API
 * @returns {Promise<Array>} - Array of supported payment methods or default methods
 */
export const fetchSupportedPaymentGateways = async () => {
  try {
    const response = await api.get("/supportedpaymentgateways");

    if (response.data && Array.isArray(response.data)) {
      return response.data;
    } else {
      showToast("Failed to fetch payment methods", ToastTypes.ERROR);
      return [];
    }
  } catch (error) {
    console.error("Error fetching payment gateways:", error);
    showToast("Could not load payment methods", ToastTypes.ERROR);
    return [];
  }
};

/**
 * Fetches user addresses from the API
 * @returns {Promise<Array>} - Array of user addresses or empty array
 */
export const fetchUserAddresses = async () => {
  try {
    const response = await api.get("/user-addresses?page=1&limit=10");

    if (response.data && Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && response.data.status === 1) {
      // When no addresses are found
      return [];
    } else {
      showToast("Failed to fetch addresses", ToastTypes.ERROR);
      return [];
    }
  } catch (error) {
    console.error("Error fetching addresses:", error);
    showToast("Could not load addresses", ToastTypes.ERROR);
    return [];
  }
};

/**
 * Creates a new address
 * @param {Object} addressData - The address data to create
 * @returns {Promise<Object|null>} - Created address object or null on failure
 */
export const createAddress = async (addressData) => {
  try {
    const payload = {
      address: addressData.street,
      state: addressData.state,
      city: addressData.city,
      country: addressData.country,
      pincode: parseInt(addressData.pincode),
      addressType: getAddressTypeValue(addressData.type),
    };

    const response = await api.post("/user-addresses", payload);

    if (response.data && response.data.ID) {
      showToast("Address added successfully", ToastTypes.SUCCESS);
      return mapApiAddressToUiAddress(response.data);
    } else {
      showToast("Failed to add address", ToastTypes.ERROR);
      return null;
    }
  } catch (error) {
    console.error("Error creating address:", error);
    showToast("Could not create address", ToastTypes.ERROR);
    return null;
  }
};

/**
 * Updates an existing address
 * @param {Object} addressData - The address data to update
 * @returns {Promise<Object|null>} - Updated address object or null on failure
 */
export const updateAddress = async (addressData) => {
  try {
    const payload = {
      ID: addressData.id,
      address: addressData.street,
      state: addressData.state,
      city: addressData.city,
      country: addressData.country,
      pincode: parseInt(addressData.pincode),
      addressType: getAddressTypeValue(addressData.type),
    };

    const response = await api.put("/user-addresses", payload);

    if (response.data && response.data.ID) {
      showToast("Address updated successfully", ToastTypes.SUCCESS);
      return mapApiAddressToUiAddress(response.data);
    } else {
      showToast("Failed to update address", ToastTypes.ERROR);
      return null;
    }
  } catch (error) {
    console.error("Error updating address:", error);
    showToast("Could not update address", ToastTypes.ERROR);
    return null;
  }
};

/**
 * Deletes an address by ID
 * @param {Number} addressId - The ID of the address to delete
 * @returns {Promise<boolean>} - Success status
 */
export const deleteAddress = async (addressId) => {
  try {
    await api.delete(`/user-addresses/${addressId}`);
    showToast("Address deleted successfully", ToastTypes.SUCCESS);
    return true;
  } catch (error) {
    console.error("Error deleting address:", error);
    showToast("Could not delete address", ToastTypes.ERROR);
    return false;
  }
};

/**
 * Maps API address format to UI address format
 * @param {Object} apiAddress - Address in API format
 * @returns {Object} - Address in UI format
 */
export const mapApiAddressToUiAddress = (apiAddress) => {
  return {
    id: apiAddress.ID,
    street: apiAddress.address,
    city: apiAddress.city,
    state: apiAddress.state,
    pincode: apiAddress.pincode.toString(),
    country: apiAddress.country,
    type: getAddressTypeLabel(apiAddress.addressType),
  };
};

/**
 * Maps API address array to UI address array
 * @param {Array} apiAddresses - Array of addresses in API format
 * @returns {Array} - Array of addresses in UI format
 */
export const mapApiAddressesToUiAddresses = (apiAddresses) => {
  return apiAddresses.map(mapApiAddressToUiAddress);
};

/**
 * Gets address type numeric value from label
 * @param {String} typeLabel - Address type label (Home, Work, Other)
 * @returns {Number} - Address type value (0, 1, 2)
 */
export const getAddressTypeValue = (typeLabel) => {
  switch (typeLabel) {
    case "Work":
      return 1;
    case "Other":
      return 2;
    case "Home":
    default:
      return 0;
  }
};

/**
 * Gets address type label from numeric value
 * @param {Number} typeValue - Address type value (0, 1, 2)
 * @returns {String} - Address type label (Home, Work, Other)
 */
export const getAddressTypeLabel = (typeValue) => {
  switch (typeValue) {
    case 1:
      return "Work";
    case 2:
      return "Other";
    case 0:
    default:
      return "Home";
  }
};

/**
 * Fetches available countries from the API
 * @returns {Promise<Array>} - Array of countries or empty array
 */
export const fetchCountries = async () => {
  try {
    const response = await api.get("/countries");

    if (response.data && Array.isArray(response.data)) {
      return response.data;
    } else {
      showToast("Failed to fetch countries", ToastTypes.ERROR);
      return [];
    }
  } catch (error) {
    console.error("Error fetching countries:", error);
    showToast("Could not load countries", ToastTypes.ERROR);
    return [];
  }
};

/**
 * Fetches states for a given country from the API
 * @param {String} country - Country name
 * @returns {Promise<Object>} - Object containing states array and phone code
 */
export const fetchStates = async (country) => {
  try {
    if (!country) {
      return { states: [], phoneCode: "" };
    }

    const response = await api.get(`/states/${encodeURIComponent(country)}`);

    if (
      response.data &&
      response.data.states &&
      Array.isArray(response.data.states)
    ) {
      return {
        states: response.data.states,
        phoneCode: response.data.phoneCode || "",
      };
    } else {
      showToast("Failed to fetch states", ToastTypes.ERROR);
      return { states: [], phoneCode: "" };
    }
  } catch (error) {
    console.error("Error fetching states:", error);
    showToast("Could not load states", ToastTypes.ERROR);
    return { states: [], phoneCode: "" };
  }
};

export const applyCouponApi = async (coupon, orderId) => {
  try {
    const result = await api.post(`/discounts/${coupon}/apply/${orderId}`);
    return result;
  } catch (error) {
    console.log(error);
    return error.response.data;
  }
};

export const removeCouponApi = async (orderDetail) => {
  let body = JSON.stringify({
    ID: orderDetail.ID,
    customerId: orderDetail.customerId,
    discountCode: "",
  });

  try {
    await api.put(`/orders`, body);
  } catch (error) {
    console.log(error);
  }
};

export const updateOrderPaymentMethodApi = async (
  orderId,
  customerId,
  customerName,
  addressId,
  paymentMethod
) => {
  const body = JSON.stringify({
    ID: orderId,
    customerId: customerId,
    customerName: customerName,
    shippingAddressId: addressId,
    billingAdressId: addressId,
    paymentMethod: paymentMethod,
  });

  try {
    const response = await api.put(`/orders`, body);
    if (response.data.ID) {
      return response.data;
    }
  } catch (error) {
    console.error("Error updating payment method:", error);
    return null;
  }
};

export const updateAddressApi = async (
  orderId,
  customerId,
  customerName,
  email,
  addressId
) => {
  let body = JSON.stringify({
    ID: orderId,
    customerId: customerId,
    customerName: customerName,
    shippingAddressId: addressId,
    billingAdressId: addressId,
  });

  if (email) {
    body = JSON.stringify({
      ID: orderId,
      customerId: customerId,
      customerName: customerName,
      shippingAddressId: addressId,
      billingAdressId: addressId,
      customerEmail: email,
    });
  }
  try {
    const response = await api.put(`/orders`, body);
    if (response?.data?.ID) {
      return response?.data;
    }
  } catch (error) {
    console.error("Error updating order:", error);
    return null;
  }
};
